import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faDownload, faFilePdf, faFileWord } from "@fortawesome/free-solid-svg-icons";
import { getCandidateProfileById } from "../../Api/apiService";
import "./ViewResume.css";
import Logo from "../../Images/NucleusTeq Logo.png";
import html2pdf from "html2pdf.js";
import Docxtemplater from "docxtemplater";
import { saveAs } from "file-saver";
import PizZip from "pizzip";

const ViewResumeCandidate = () => {
  const { candidateId } = useParams();
  const [resume, setResume] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchResume = async () => {
      setLoading(true);
      try {
        const response = await getCandidateProfileById(candidateId);
        setResume(response.data);
      } catch (error) {
        setError("Error fetching resume data.");
      } finally {
        setLoading(false);
      }
    };

    if (candidateId) {
      fetchResume();
    }
  }, [candidateId]);

  const downloadPDF = () => {
    const element = document.getElementById("resume-content");
    const opt = {
      margin: [10, 0, 23, 0],
      filename: `${resume?.name || "Candidate"}_Resume.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2, scrollX: 0, scrollY: 0, useCORS: true },
      jsPDF: { unit: "pt", format: "a4", orientation: "portrait" },
    };

    html2pdf().set(opt).from(element).save();
  };

  const previewPDF = () => {
    const element = document.getElementById("resume-content");
    const opt = {
      margin: [10, 0, 23, 0],
      filename: `${resume?.name || "Candidate"}_Resume.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { allowTaint: true, letterRendering: true, scale: 2, scrollX: 0, scrollY: 0, useCORS: true },
      jsPDF: { unit: "pt", format: "a4", orientation: "portrait" },
      autoPaging: "text",
      pagebreak: {
        mode: ["css", "legacy"],
      },
    };

    // Open the PDF in a new tab
    html2pdf()
      .set(opt)
      .from(element)
      .toPdf()
      .get("pdf")
      .then((pdf) => {
        const pdfBlob = pdf.output("bloburl");
        window.open(pdfBlob, "_blank");
      });
  };

  const downloadWord = () => {
    fetch("/resume_template.docx")
      .then((response) => {
        if (!response.ok) {
          throw new Error("Failed to load the Word template file.");
        }
        return response.arrayBuffer();
      })
      .then((content) => {
        // Use PizZip to load the content
        const zip = new PizZip(content);
        const doc = new Docxtemplater(zip, {
          paragraphLoop: true,
          linebreaks: true,
        });

        // Replace placeholders in the template with dynamic data
        doc.render({
          name: resume?.name || "Candidate",
          contactNo: resume?.contactNo || "N/A",
          email: resume?.email || "N/A",
          objective: resume?.objective || "N/A",
          professionalSummary: resume?.profileData?.professionalSummary || "N/A",
          technicalSkills: resume?.profileData?.technicalSkills || [],
          professionalExperience: resume?.profileData?.professionalExperience || [],
          education: resume?.profileData?.education || [],
          certificates: resume?.profileData?.certificates || [],
        });

        // Generate the Word document as a blob
        const blob = doc.getZip().generate({
          type: "blob",
          mimeType: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        });

        console.log("Docxtemplater Data:", doc.getFullText());

        // Save the Word document
        saveAs(blob, `${resume?.name || "Candidate"}_Resume.docx`);
      })
      .catch((error) => {
        console.error("Error loading or generating Word template:", error);
      });
  };

  const formatExperienceDetails = (details) => {
    const specialCharactersRegex = /^[^a-zA-Z0-9]/;
    return details?.split(".").map((sentence, index) => {
      let trimmedSentence = sentence.trim();
      if (specialCharactersRegex.test(trimmedSentence)) {
        trimmedSentence = trimmedSentence.replace(specialCharactersRegex, "");
      }
      return trimmedSentence ? (
        <li key={index}>
          {trimmedSentence}
          {trimmedSentence.endsWith(".") ? "" : "."}
        </li>
      ) : null;
    });
  };

  const formatDate = (date) => {
    if (!date) return "";

    if (date.toLowerCase() === "present" || date.toLowerCase() === "current" || date.toLowerCase() === "now" || date.toLowerCase() === "till date") {
      return "Present";
    }

    const monthYearRegex = /^([a-zA-Z]{3,9})['’]?(\d{2}|\d{4})$/; // Matches formats like "Jan'22" or "Jan22"
    const match = date.match(monthYearRegex);

    if (match) {
      const month = match[1]; // Extract the month (e.g., "Jan")
      const year = `20${match[2]}`; // Extract the year (e.g., "22" -> "2022")
      const monthIndex = new Date(`${month} 1, ${year}`).getMonth(); // Get the month index (0-11)
      const options = { year: "numeric", month: "2-digit" };
      return new Date(year, monthIndex, 1).toLocaleDateString("en-GB", options).replace(/\//g, "/");
    }

    const numericMonthYearRegex = /^(\d{1,2})\/(\d{4})$/;
    const numericMatch = date.match(numericMonthYearRegex);

    if (numericMatch) {
      const month = numericMatch[1]; // Extract the month
      const year = numericMatch[2]; // Extract the year
      const options = { year: "numeric", month: "2-digit" };
      return new Date(year, month - 1, 1).toLocaleDateString("en-GB", options).replace(/\//g, "/");
    }

    const isoDateRegex = /^(\d{4})-(\d{2})-(\d{2})$/;
    const isoMatch = date.match(isoDateRegex);
    if (isoMatch) {
      const year = parseInt(isoMatch[1], 10);
      const month = parseInt(isoMatch[2], 10) - 1;
      const day = parseInt(isoMatch[3], 10);
      return new Date(year, month, day).toLocaleDateString("en-GB", {
        year: "numeric",
        month: "short",
        day: "2-digit",
      });
    }

    const usDateRegex = /^(\d{2})-(\d{2})-(\d{4})$/;
    const usMatch = date.match(usDateRegex);
    if (usMatch) {
      const month = parseInt(usMatch[1], 10) - 1;
      const day = parseInt(usMatch[2], 10);
      const year = parseInt(usMatch[3], 10);
      return new Date(year, month, day).toLocaleDateString("en-GB", {
        year: "numeric",
        month: "short",
        day: "2-digit",
      });
    }

    const euDateRegex = /^(\d{2})-(\d{2})-(\d{4})$/;
    const euMatch = date.match(euDateRegex);
    if (euMatch) {
      const day = parseInt(euMatch[1], 10);
      const month = parseInt(euMatch[2], 10) - 1;
      const year = parseInt(euMatch[3], 10);
      return new Date(year, month, day).toLocaleDateString("en-GB", {
        year: "numeric",
        month: "short",
        day: "2-digit",
      });
    }

    const fullMonthYearRegex = /^([a-zA-Z]{3,9}) (\d{4})$/;
    const fullMonthYearMatch = date.match(fullMonthYearRegex);
    if (fullMonthYearMatch) {
      const month = fullMonthYearMatch[1];
      const year = parseInt(fullMonthYearMatch[2], 10);
      const monthIndex = new Date(`${month} 1, ${year}`).getMonth();
      return new Date(year, monthIndex, 1).toLocaleDateString("en-GB", {
        year: "numeric",
        month: "short",
      });
    }

    return date;
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>{error}</div>;
  if (!resume) return <div>No resume data available</div>;

  console.log(resume);

  const sortedExperience = resume.profileData?.professionalExperience?.sort((a, b) => new Date(b.endDate) - new Date(a.endDate)) || [];

  const sortedEducation =
    resume.profileData?.education
      ?.map((edu) => ({
        ...edu,

        duration: edu.duration?.trim().endsWith("-") ? edu.duration.replace(/-\s*$/, "") : edu.duration,
      }))
      .sort((a, b) => {
        const endDateA = a.duration?.split(" - ")[1] || a.duration;
        const endDateB = b.duration?.split(" - ")[1] || b.duration;

        return new Date(endDateB) - new Date(endDateA);
      }) || [];

  return (
    <>
      <div className="btn-container">
        <Link to="/candidateResumeList" className="icon-button" title="back">
          <FontAwesomeIcon icon={faArrowLeft} />
        </Link>
        <button onClick={previewPDF} className="icon-button" title="preview pdf">
          <FontAwesomeIcon icon={faFilePdf} />
        </button>
        <button onClick={downloadWord} className="icon-button" title="download word">
          <FontAwesomeIcon icon={faFileWord} />
        </button>
        <button onClick={downloadPDF} className="icon-button" title="download pdf">
          <FontAwesomeIcon icon={faDownload} />
          {/* <FontAwesomeIcon icon={faFilePdf} /> */}
        </button>
      </div>
      <hr />
      <div className="resume-container" id="resume-content">
        <div className="logo-container">
          <img src={Logo} alt="logo" className="logo" />
        </div>
        <div className="personal-info">
          <h1>{resume.name || "Unknown"}</h1>
          {/* {resume.contactNo && <div className="contact">📞 {resume.contactNo}</div>}
          {resume.email && <div className="contact">✉️ {resume.email}</div>} */}
        </div>

        {resume.objective && resume.objective.trim().length > 0 && (
          <div>
            <h2 className="section-title">Objective</h2>
            <p className="selection-obj">{resume.objective}</p>
          </div>
        )}

        {resume.profileData?.professionalSummary?.trim()?.length > 0 && (
          <div>
            <h2 className="section-title">Professional Summary</h2>
            <p className="section-para">{resume.profileData.professionalSummary}</p>
          </div>
        )}

        {(resume.profileData?.technicalSkills?.technology?.length > 0 ||
          resume.profileData?.technicalSkills?.programming?.length > 0 ||
          resume.profileData?.technicalSkills?.tools?.length > 0) && (
          <div>
            <h2 className="section-title">Technical Skills</h2>

            {resume.profileData?.technicalSkills?.technology?.length > 0 && (
              <>
                <h3 className="sub-section-title">Technology</h3>
                <ul className="bullet-section-tech grid-layout">
                  {resume.profileData.technicalSkills.technology.map((tech, index) => (
                    <li key={index}>{tech}</li>
                  ))}
                </ul>
              </>
            )}

            {resume.profileData?.technicalSkills?.programming?.length > 0 && (
              <>
                <h3 className="sub-section-title">Programming</h3>
                <ul className="bullet-section-tech grid-layout">
                  {resume.profileData.technicalSkills.programming.map((prog, index) => (
                    <li key={index}>{prog}</li>
                  ))}
                </ul>
              </>
            )}

            {resume.profileData?.technicalSkills?.tools?.length > 0 && (
              <>
                <h3 className="sub-section-title">Tools</h3>
                <ul className="bullet-section-tech">
                  {resume.profileData.technicalSkills.tools.map((tool, index) => (
                    <li key={index}>{tool}</li>
                  ))}
                </ul>
              </>
            )}
          </div>
        )}
        {sortedExperience?.length > 0 && (
          <div>
            <h2 className="section-title">Professional Experience</h2>
            {sortedExperience.map((experience, index) => (
              <div key={index} className="professional-experience">
                <h3 className="experience-heading">
                  {experience.jobTitle || "Position"} at {experience.companyName || "Company"}
                  <span className="experience-dates">
                    ({formatDate(experience.startDate)} - {formatDate(experience.endDate)})
                  </span>
                </h3>
                {experience.projectName?.trim()?.length > 0 && (
                  <p className="experience-project">
                    <strong>Project:</strong> {experience.projectName}
                  </p>
                )}
                {experience.techStack?.trim()?.length > 0 && (
                  <p className="experience-project">
                    <strong>Technologies Used:</strong> {experience.techStack}
                  </p>
                )}
                <ul className="bullet-section">{formatExperienceDetails(experience.details)}</ul>
              </div>
            ))}
          </div>
        )}

        {sortedEducation?.length > 0 && (
          <div>
            <h2 className="section-title">Education</h2>
            {sortedEducation.map((education, index) => (
              <div key={index} className="education">
                <h3 className="experience-heading">
                  {education.course || "Course"} from {education.collegeName || "Institution"}
                </h3>
                {education.duration && education.duration.trim().length > 0 && (
                  <p
                    className={
                      education.duration.includes("%") || education.duration.includes("CGPA") ? "experience-duration" : "experience-duration"
                    }
                  >
                    {education.duration.includes("%") || education.duration.includes("CGPA") ? (
                      <>Percentage/CGPA: {education.duration}</>
                    ) : (
                      <>Duration: {education.duration}</>
                    )}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}

        {resume.profileData?.certificates?.length > 0 && (
          <div>
            <h2 className="section-title">Certificates</h2>
            <ul className="bullet-section">
              {resume.profileData.certificates.map((certificate, index) => (
                <li key={index}>{certificate}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </>
  );
};

export default ViewResumeCandidate;
